import React, { useCallback, useEffect, useState } from 'react';
import { Form, Checkbox } from 'antd';
import {
  AddEditPFAdminModal,
  ClientAnalytics,
  FormInput,
  IconDoer,
  IconPen,
  IconPlus,
  IconSmallArrow,
  IconSoberLiving,
  OrangeButton,
} from 'components';
import classNames from 'classnames';
import { getRequestData } from 'api/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { endpoints } from 'api/endpoints';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { getIdFromPath } from 'utils/helpers';
import { ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';

const defaultFormData = {
  slh: {
    name: '',
    region: '',
    rank: '',
    gender: '',
    doer: false,
    parent: '',
    bed_count: null,
    address: '',
    website: '',
    weekly_rate: null,
    type: 'no_relationship',
    id: null,
  },
};

export const ViewChildPartner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [totalBeds, setTotalBeds] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [managers, setManagers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [managerData, setManagerData] = useState(null);
  const [form] = Form.useForm();
  const partnerID = getIdFromPath(location.pathname);

  const fetchPartnerData = useCallback(async () => {
    const { data } = await getRequestData(endpoints.getPartner(partnerID), ROLE_TYPES.slh_manager);
    if (data) {
      const {
        name,
        region,
        rank,
        gender,
        doer,
        parent,
        bed_count,
        address,
        website,
        weekly_rate,
        fiscal_start_dow,
        type,
        id,
      } = data;
      const updatedFormData = {
        slh: {
          ...defaultFormData.slh,
          name,
          region,
          parent,
          bed_count,
          address,
          website,
          weekly_rate,
          fiscal_start_dow,
          doer: doer ?? false,
          gender: gender.split(','),
          rank: rank ? String(rank) : rank,
          id,
          type,
          clients: data.clients,
        },
      };
      setTotalBeds(bed_count || totalBeds);
      setFormData(updatedFormData);
      setManagers(data.manager);
      form.setFieldsValue(updatedFormData);
    }
  }, [form, partnerID, totalBeds]);

  useEffect(() => {
    fetchPartnerData();
  }, [fetchPartnerData]);
  console.log('formData', formData);
  return (
    totalBeds && (
      <div className="flex flex-col w-full h-full overflow-y-auto gap-y-6">
        <Form
          form={form}
          name="create-client-by-admin"
          initialValues={formData}
          className="flex flex-col w-full gap-6"
          requiredMark={false}
        >
          <div className="flex justify-between w-full">
            <div className="flex items-center justify-between gap-x-4">
              <div
                onClick={() => navigate(-1)}
                className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
              >
                <IconSmallArrow className="w-2 h-4" />
              </div>
              <p className="nunito-title">{formData.slh?.name}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-start gap-6 ">
            <div className="flex flex-col w-full base-block pt-[30px]">
              <div className="flex justify-between items-center pr-[30px] mb-4">
                <p className="nunito-title px-[30px] pb-[11px]">SLH</p>
                <OrangeButton
                  onClick={() => navigate(`/partner/${formData.slh.id}`)}
                  icon={<IconPen />}
                  label="Edit"
                />
              </div>
              <div
                className={classNames(
                  'pointer-events-none flex flex-wrap relative border-t border-color-lightBlue p-[30px] pt-3 gap-x-8 gap-y-6',
                  'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                )}
              >
                <FormInput
                  label="Name*"
                  name={['slh', 'name']}
                  placeholder="First Name"
                  required
                  errorMessage="Please enter valid name"
                  className="w-[calc(50%-16px)]"
                  value={formData.slh.name}
                  disabled
                />
                <FormInput
                  label="Region"
                  name={['slh', 'region']}
                  placeholder="Enter region"
                  className="w-[calc(50%-16px)]"
                  value={formData.slh.region}
                  disabled
                />
                <div className="flex w-[calc(50%-16px)]">
                  <div className="flex flex-col gap-y-2">
                    <p className="text-14 font-medium">Beds Count*</p>
                    <div className="flex items-center gap-x-1">
                      <input
                        type="number"
                        className="w-10 h-10 border border-divider outline-divider outline-1 text-center rounded-ten text-14"
                        value={totalBeds}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <FormInput
                  label="Address"
                  name={['slh', 'address']}
                  placeholder="Enter address"
                  className="w-[calc(50%-16px)]"
                  disabled
                />
                <div className="flex flex-col gap-y-2 w-[calc(50%-16px)]">
                  <p className="text-14 font-medium">Gender*</p>
                  <Form.Item name={['slh', 'gender']} required disabled>
                    <Checkbox.Group className="w-full gap-x-4">
                      {formData?.slh?.gender?.map((gender) => (
                        <Checkbox
                          key={gender}
                          className="max-w-[8rem] px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2 flex-1"
                          value={gender}
                        >
                          {gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : 'Other'}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                </div>
                <div className="flex gap-x-6 w-[calc(50%-16px)]">
                  <FormInput
                    label="Website"
                    name={['slh', 'website']}
                    placeholder="Enter website"
                    className="w-[calc(50%-16px)]"
                    disabled
                  />

                  <FormInput
                    label="Weekly Rate*"
                    type="number"
                    name={['slh', 'weekly_rate']}
                    placeholder="$"
                    required
                    className="w-[calc(50%-16px)]"
                    disabled
                  />
                </div>
                <div className="flex gap-x-6 justify-between w-full">
                  <div className="flex flex-col gap-y-2 w-[calc(50%-16px)]  ">
                    <p className="text-14 font-medium">D.O.E.R. Program*</p>
                    <div className="flex items-center gap-x-3">
                      <IconDoer className="w-8 h-8" />
                      <Checkbox
                        className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2 w-fit"
                        checked={formData.slh.doer}
                      >
                        D.O.E.R. Support
                      </Checkbox>
                    </div>
                  </div>

                  <FormInput
                    label="Fiscal Week Start Day"
                    value={formData.slh.fiscal_start_dow}
                    className="w-[calc(50%-16px)]"
                    disabled
                  />
                </div>
                <div className="flex flex-col flex-1 gap-y-2 w-[calc(50%-16px)]">
                  <div className="flex justify-flex-start items-center">
                    <p className="text-14 font-medium">Type</p>
                  </div>
                  <FormInput
                    className="w-full"
                    inputClassName="capitalize"
                    value={formData.slh.type.replace('_', ' ')}
                    disabled
                  />
                </div>
                {formData.slh.type === SLH_TYPES.child && (
                  <div className="flex flex-col flex-1 gap-y-2">
                    <div className="flex justify-flex-start items-center">
                      <p className="text-14 font-medium">Parent</p>
                    </div>
                    <FormInput
                      inputClassName="capitalize"
                      value={formData.slh?.parent?.name}
                      disabled
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
        <div className="flex flex-col base-block pt-[30px]">
          <div className="flex justify-between items-center pr-[30px] mb-4">
            <p className="nunito-title px-[30px] pb-[11px]">SLH MANAGERS</p>
            <OrangeButton
              icon={<IconPlus />}
              label="Add new"
              onClick={() => setIsCreateModalOpen(true)}
            />
            {isCreateModalOpen && (
              <AddEditPFAdminModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                partnerID={formData?.slh?.id}
                onSuccess={fetchPartnerData}
                role={ROLE_TYPES.slh_manager}
              />
            )}
          </div>
          <div className="flex flex-col gap-y-6 px-[30px] py-[30px] border-t border-color-lightBlue relative">
            <div className="flex">
              <p className="text-14 text-lightBlue w-[30%]">Manager</p>
              <p className="text-14 text-lightBlue w-[30%]">SLH</p>
              <p className="text-14 text-lightBlue w-[11%]">Type</p>
              <p className="text-14 text-lightBlue w-[11%]">Phone</p>
              <p className="text-14 text-lightBlue w-[11%]">Status</p>
              <p className="text-14 text-lightBlue w-[7%] text-right">Action</p>
            </div>
            <div className="flex flex-col">
              {managers.map((manager) => (
                <div
                  key={manager.id}
                  className="flex items-center py-2 border-b border-color-lightBlue"
                >
                  <div className="flex gap-x-1 w-[30%]">
                    <img
                      className="self-center w-8 h-8 rounded-xl"
                      src={manager.avatar ?? DefaultAvatar}
                      alt="profile"
                    />
                    <div className="flex flex-col ml-3">
                      <p className="text-14 text-black">
                        {manager.firstname} {manager.lastname}
                      </p>
                      <p className="text-12 text-lightBlue">{manager.email}</p>
                    </div>
                  </div>
                  <p className="text-14 text-black w-[30%] gap-x-1 flex items-center">
                    <IconSoberLiving />
                    {formData.slh.name}
                  </p>
                  <p className="text-14 text-black w-[11%]">Manager</p>
                  <p className="text-14 text-black w-[11%]">
                    {manager.phone ? `${manager.country_code}${manager.phone}` : '-'}
                  </p>
                  <p className="text-14 text-black w-[11%] capitalize">{manager.status}</p>
                  <div className="flex justify-end w-[7%] cursor-pointer">
                    <IconPen
                      color={COLORS.blue}
                      className="w-8 h-8 bg-light p-1 rounded-lg"
                      onClick={() => {
                        setIsEditModalOpen(true);
                        setManagerData(manager);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {isEditModalOpen && (
              <AddEditPFAdminModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setIsEditModalOpen(false);
                }}
                adminData={managerData}
                isEditMode={true}
                role={ROLE_TYPES.slh_manager}
                partnerID={formData?.slh?.id}
              />
            )}
          </div>
        </div>
        <ClientAnalytics
          clients={formData.slh.clients}
          seeAllUrl="/clients"
          navigateOptions={{
            state: { slhParentId: partnerID },
          }}
        />
      </div>
    )
  );
};
